import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useParams} from "react-router-dom";
import {APP_ALERT_CENTER_API_ENDPOINT} from "../../../../constants/api_urls";
import {getUserToken} from "utils/functions/userToken";
import {Builder, Query, Utils as QbUtils} from '@react-awesome-query-builder/ui';
import {buildFieldMap, sqlToJsonLogic, transformFieldNames} from '../../Functions/index';
import '../AlertForm.scss';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import AlertTextField from "features/alert-center/AlertForm/FormComponents/AlertTextField";
import AlertLevelSelector from "features/alert-center/AlertForm/FormComponents/AlertLevelSelector";
import HotelSelection from "features/alert-center/AlertForm/FormComponents/HotelSelection";
import RecipientsField from "features/alert-center/AlertForm/FormComponents/RecipientsField";
import GroupSelection from "features/alert-center/AlertForm/FormComponents/GroupSelection"
import Checkbox from '@mui/material/Checkbox';
import '@react-awesome-query-builder/ui/css/styles.css';
import '@react-awesome-query-builder/ui/css/compact_styles.css';
import CustomFormControl from "../../Dropdown/CustomFormControl";
import ListSubheader from '@mui/material/ListSubheader';
import QueryBuilderConfig from "./QueryBuilderConfig";
import CustomButtonWithIcon from "../../../../components/CustomButtonWithIcon/Buttons";
import {getUserDetailsFromToken} from 'utils/functions/userTokenDetails';

const AlertDisplayAdsForm = ({ onClose, alertData, actionType, showSnackbar, refreshData }) => {
    const { idaccount } = useParams();
    const jwtToken = getUserToken();
    const apiEndpoint = `${APP_ALERT_CENTER_API_ENDPOINT}/api/alert-center-metrics/display-ads/metrics-list`;
    const [alertName, setAlertName] = useState(alertData?.name || '');
    const [segmentList, setSegmentList] = useState(alertData?.segmentList || []);
    const [alertRecipients, setAlertRecipients] = useState(alertData?.recipients || []);
    const [additionnalColumns, setAdditionnalColumns] = useState(alertData?.additionnalColumns || []);
    const [alertPeriod, setAlertPeriod] = useState(alertData?.alertPeriod || 1);
    const [alertLevel, setAlertLevel] = useState(alertData?.alertLevel || "ACCOUNT");
    const [alertCondition, setAlertCondition] = useState(alertData?.alertCondition || null);
    const [tree, setTree] = useState(null);
    const [optionsForAdditionalColumns, setOptionsForAdditionalColumns] = useState([]);
    const tagifyRef = useRef(null);
    const [fieldMap, setFieldMap] = useState({});
    const [config, setConfig] = useState(QueryBuilderConfig);
    const [errors, setErrors] = useState({ alertName: false, alertPeriod: false, alertRecipients: false, alertCondition: false, additionnalColumns: false });
    const [isHotelSlidingPanelOpen, setIsHotelSlidingPanelOpen] = useState(false);
    const [isGroupSlidingPanelOpen, setIsGroupSlidingPanelOpen] = useState(false);
    const [selectedHotels, setSelectedHotels] = useState([]);
    const [hotelRowSelection, setHotelRowSelection] = useState({});
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [groupRowSelection, setGroupRowSelection] = useState({});
    const [errorSelectedHotels, setErrorSelectedHotels] = useState(false);
    const [errorSelectedGroups, setErrorSelectedGroups] = useState(false);
    const [userDetails, setUserDetails] = useState({
        username: 'N/A',
        roles: 'N/A',
        isMainUser: false,
        isAdminUser: false,
        isSuperAdminUser: false
    });
    let method = 'POST';
    let apiActionEndpoint = `${APP_ALERT_CENTER_API_ENDPOINT}/api/alert-center-metrics/display-ads/add/${idaccount}`;
    if (actionType === 'edit') {
        method = 'PUT';
        apiActionEndpoint = `${APP_ALERT_CENTER_API_ENDPOINT}/api/alert-center-metrics/display-ads/edit/${idaccount}/${alertData.id}`;
    }

    useEffect(() => {
        const token = getUserToken();
        const { UserDetails } = getUserDetailsFromToken(token);
        setUserDetails(UserDetails);
    }, []);

    const submitDisplayAdsAlert = (requestBody) => {
        fetch(apiActionEndpoint, {
            method: method,
            headers: {
                'Authorization': `Bearer ${jwtToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
        })
            .then((response) => {
                if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
                return response.json();
            })
            .then(() => {
                showSnackbar('Alert saved successfully');
                if (refreshData) refreshData();
                onClose();
            })
            .catch((error) => {
                showSnackbar('Error saving alert: ' + error.message, 'error');
            });
    };

    const handleHotelSelect = useCallback((selectedHotels) => {
        setSelectedHotels(selectedHotels);
    }, []);

    const handleGroupSelect = useCallback((selectedGroups) => {
        setSelectedGroups(selectedGroups);
    }, []);

    const alertLevelLabels = {
        ACCOUNT: "Account",
        HOTEL: "Hotel",
        GROUP: "Group"
    };

    const isAdminOrMainUser = userDetails.isAdminUser || userDetails.isSuperAdminUser || userDetails.isMainUser;

    const handleRemoveHotel = (hotelId) => {
        setSelectedHotels(prevHotels => prevHotels.filter(h => h.id !== hotelId));
        setHotelRowSelection(prevSelection => {
            const newSelection = { ...prevSelection };
            delete newSelection[hotelId];
            return newSelection;
        });
    };

    const handleRemoveGroups = (groupId) => {
        setSelectedGroups(prevGroups => prevGroups.filter(h => h.id !== groupId));
        setGroupRowSelection(prevSelection => {
            const newSelection = { ...prevSelection };
            delete newSelection[groupId];
            return newSelection;
        });
    };

    const handleClearAllHotels = () => {
        setSelectedHotels([]);
        setHotelRowSelection({});
    };

    const handleClearAllGroups = () => {
        setSelectedGroups([]);
        setGroupRowSelection({});
    };




    const handleRecipientsChange = useCallback((e) => {
        let parsedValue;
        try {
            parsedValue = JSON.parse(e.detail.value);
        } catch {
            parsedValue = [];
        }
        setAlertRecipients(parsedValue.map(tag => tag.value));
    }, []);

    const handleSegmentChange = (e) => {
        const value = e.target.value;
        setSegmentList(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
    };

    const generateFieldsFromMetrics = (metrics) => {
        const groupedMetrics = metrics.reduce((acc, metric) => {
            const { metric_group } = metric;
            acc[metric_group] = acc[metric_group] || [];
            acc[metric_group].push(metric);
            return acc;
        }, {});

        const fields = {};
        for (const groupName in groupedMetrics) {
            const subfields = {};
            groupedMetrics[groupName].forEach(({ metric_label, metric_column }) => {
                subfields[metric_column] = {
                    fieldSettings: { groupTitle: groupName },
                    label: metric_label,
                    type: 'number',
                    valueSources: ['value'],
                    preferWidgets: ['number'],
                    operators: ['equal', 'less', 'greater', 'less_or_equal', 'greater_or_equal'],
                    widgets: {
                        number: { operators: ['equal', 'less', 'greater', 'less_or_equal', 'greater_or_equal'] },
                    },
                };
            });
            fields[groupName] = { type: '!struct', label: groupName, subfields };
        }
        return fields;
    };

    useEffect(() => {
        const fetchLinkedHotels = async () => {
            if (alertData && actionType === 'edit' && alertLevel === 'HOTEL') {
                try {
                    const res = await fetch(`${APP_ALERT_CENTER_API_ENDPOINT}/api/alert-center-metrics/list-linked-hotels/${alertData.id}`, {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${jwtToken}`,
                            'Content-Type': 'application/json',
                        }
                    });
                    const hotels = await res.json();
                    setSelectedHotels(hotels);

                    const selectionMap = {};
                    hotels.forEach(hotel => {
                        selectionMap[hotel.id] = true;
                    });
                    setHotelRowSelection(selectionMap);
                } catch (error) {
                    console.error("Error fetching linked hotels:", error);
                }
            }
        };

        fetchLinkedHotels();
    }, [alertData, actionType, alertLevel]);

    useEffect(() => {
        const fetchLinkedGroups = async () => {
            if (alertData && actionType === 'edit' && alertLevel === 'GROUP') {
                try {
                    const res = await fetch(`${APP_ALERT_CENTER_API_ENDPOINT}/api/alert-center-metrics/list-linked-groups/${alertData.id}`, {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${jwtToken}`,
                            'Content-Type': 'application/json',
                        }
                    });
                    const groups = await res.json();
                    setSelectedGroups(groups);

                    const selectionMap = {};
                    groups.forEach(group => {
                        selectionMap[group.id] = true;
                    });
                    setGroupRowSelection(selectionMap);
                } catch (error) {
                    console.error("Error fetching linked groups:", error);
                }
            }
        };

        fetchLinkedGroups();
    }, [alertData, actionType, alertLevel]);


    useEffect(() => {
        const fetchMetrics = async () => {
            try {
                const res = await fetch(apiEndpoint, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${jwtToken}`,
                        'Content-Type': 'application/json',
                    }
                });
                const result = await res.json();
                const fields = generateFieldsFromMetrics(result.metric_name || []);
                setConfig(prev => ({
                    ...QueryBuilderConfig,
                    fields,
                    settings: {
                        ...prev.settings,
                        showNot: false,
                        canReorder: false,
                        groupOperators: ['AND', 'OR'],
                        forceShowConj: true,
                        formatField: (field) => field.split(".").pop(),
                    },
                }));
                setFieldMap(buildFieldMap(fields));
                setOptionsForAdditionalColumns(result.metric_name || []);
            } catch {}
        };
        fetchMetrics();
    }, [apiEndpoint, jwtToken]);

    useEffect(() => {
        if (alertCondition && config.fields && Object.keys(config.fields).length > 0) {
            const rawLogic = sqlToJsonLogic(alertCondition);
            const transformedLogic = transformFieldNames(rawLogic, fieldMap);
            const generatedTree = QbUtils.loadFromJsonLogic(transformedLogic, config);
            setTree(generatedTree);
        } else {
            const emptyTree = QbUtils.checkTree(QbUtils.loadTree({ id: QbUtils.uuid(), type: 'group' }), config);
            setTree(emptyTree);
        }
    }, [alertCondition, config, fieldMap]);


    useEffect(() => {
        if (alertLevel === "HOTEL") {
            setSelectedGroups([]);
            setGroupRowSelection([]);
        } else if (alertLevel === "GROUP") {
            setSelectedHotels([]);
            setHotelRowSelection([]);
        } else {
            setSelectedGroups([]);
            setGroupRowSelection([]);
            setSelectedHotels([]);
            setHotelRowSelection([]);
        }
    }, [alertLevel]);

    const onChange = (immutableTree) => setTree(immutableTree);

    const handleSubmit = async () => {
        const sqlFormat = QbUtils.sqlFormat(tree, config);
        const body = {
            alert_name: alertName,
            segment_list: segmentList,
            alert_recipients: alertRecipients,
            additionnal_columns: additionnalColumns || [],
            alert_condition: sqlFormat,
            alert_period: alertPeriod,
            alert_level: alertLevel,
            alert_hotels_selection: selectedHotels.map(hotel => hotel.id),
            alert_groups_selection: selectedGroups.map(group => group.id)
        };

        const missingHotels = alertLevel === "HOTEL" && selectedHotels.length === 0;
        const missingGroups = alertLevel === "GROUP" && selectedGroups.length === 0;

        const newErrors = {
            alertName: !body.alert_name,
            alertPeriod: !body.alert_period,
            alertRecipients: body.alert_recipients.length === 0,
            alertCondition: !body.alert_condition,
            additionnalColumns: body.additionnal_columns.length === 0,
        };
        setErrors(newErrors);
        setErrorSelectedHotels(missingHotels);
        setErrorSelectedGroups(missingGroups)

        if (Object.values(newErrors).some(e => e) || missingHotels || missingGroups) {
            showSnackbar('Please fill in all required fields', 'error');
        } else {
            submitDisplayAdsAlert(body);
        }
    };

    return (
        <div className="alert-form">
            <div className="top-side">
                <AlertTextField
                    placeholder="Alert Name:"
                    value={alertName}
                    onChange={(e) => setAlertName(e.target.value)}
                    error={errors.alertName}
                    helperText="This field is required"
                />
            </div>

            <AlertLevelSelector
                value={alertLevel}
                onChange={(e) => setAlertLevel(e.target.value)}
                isAdminOrMainUser={isAdminOrMainUser}
                error={errors.alertLevel}
            />

            {alertLevel === "HOTEL" && (
                <HotelSelection
                    isOpen={isHotelSlidingPanelOpen}
                    setOpen={setIsHotelSlidingPanelOpen}
                    selectedHotels={selectedHotels}
                    handleRemoveHotel={handleRemoveHotel}
                    handleClearAllHotels={handleClearAllHotels}
                    handleHotelSelect={handleHotelSelect}
                    hotelRowSelection={hotelRowSelection}
                    setHotelRowSelection={setHotelRowSelection}
                    idaccount={idaccount}
                    jwtToken={jwtToken}
                    showError={errorSelectedHotels}
                />
            )}

            {alertLevel === "GROUP" && isAdminOrMainUser && (

                <GroupSelection
                    isOpen={isGroupSlidingPanelOpen}
                    setOpen={setIsGroupSlidingPanelOpen}
                    selectedGroups={selectedGroups}
                    handleRemoveGroup={handleRemoveGroups}
                    handleClearAllGroups={handleClearAllGroups}
                    handleGroupSelect={handleGroupSelect}
                    groupRowSelection={groupRowSelection}
                    setGroupRowSelection={setGroupRowSelection}
                    idaccount={idaccount}
                    jwtToken={jwtToken}
                    showError={errorSelectedGroups}
                />
            )}

            <div className="bottom-top-side">
                <div className="alert-period-container">
                    <label>ALERT PERIOD</label>
                    <CustomFormControl
                        value={alertPeriod}
                        onChange={(e) => setAlertPeriod(e.target.value)}
                        displayEmpty
                        renderValue={(selected) => {
                            if (!selected) {
                                return (
                                    <span style={{
                                        fontSize: "12px",
                                        color: "#bab7b7"
                                    }}>Number of Days</span>
                                );
                            }
                            if (selected === -1) {
                                return "Current Month";
                            }
                            return selected+ " day(s)";
                        }}
                        required
                        error={errors.alertPeriod}
                        helperText={errors.alertPeriod ? "This field is required" : ""}
                    >
                        <MenuItem disabled value="">
                            <span style={{
                                fontSize: "12px",
                                color: "black",
                                fontFamily: "Inter",
                                marginLeft: "4px"
                            }}>Number of Days</span>
                        </MenuItem>


                        {Array.from({ length: 10 }, (_, i) => i + 1).map((num) => (
                            <MenuItem key={num} value={num} sx={{ fontSize: '12px', fontFamily: "Inter", color: "black" }}>
                                {num}
                            </MenuItem>
                        ))}
                        <MenuItem key={-1} value={-1} sx={{ fontSize: '12px', fontFamily: "Inter", color: "black" }}>
                            Current Month
                        </MenuItem>
                    </CustomFormControl>
                </div>

                <div className="additional-columns-container">
                    <label>ADDITIONAL COLUMNS</label>
                    <CustomFormControl
                        multiple
                        value={additionnalColumns || []}
                        onChange={(event) => setAdditionnalColumns(event.target.value)}
                        displayEmpty
                        renderValue={(selected) => {
                            if (selected.length === 0) {
                                return (
                                    <span style={{ fontSize: "12px", fontFamily: "Inter", color: "black" }}>
                    Choose Columns
                </span>
                                );
                            }
                            return selected
                                .map((value) => {
                                    const option = optionsForAdditionalColumns.find(
                                        (opt) => opt.metric_name === value
                                    );
                                    return option ? option.metric_label : value;
                                })
                                .join(", ");
                        }}
                        required
                        error={errors.additionnalColumns}
                        helperText={errors.additionnalColumns ? "This field is required" : ""}
                        size="small"
                        sx={{ width: "280px" }}
                        MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                    >
                        <ListSubheader
                            style={{
                                fontSize: "12px",
                                fontFamily: "Inter",
                                color: "#6A76DB",
                                lineHeight: '20px',
                            }}
                        >
                            Basic Metrics
                        </ListSubheader>
                        {optionsForAdditionalColumns
                            .filter(option => option.metric_group === "Metrics")
                            .map((option) => (
                                <MenuItem
                                    key={option.metric_name}
                                    value={option.metric_name}
                                    sx={{ fontSize: "12px", color: "black", fontFamily: "Inter" }}
                                >
                                    <Checkbox
                                        checked={additionnalColumns.includes(option.metric_name)}
                                        sx={{
                                            "& .MuiSvgIcon-root": {
                                                fontSize: 12,
                                            },
                                        }}
                                    />
                                    <ListItemText
                                        primary={option.metric_label}
                                        primaryTypographyProps={{
                                            style: { fontSize: "12px", fontFamily: "Inter", color: "black" },
                                        }}
                                    />
                                </MenuItem>
                            ))}
                        <ListSubheader
                            style={{
                                fontSize: "12px",
                                fontFamily: "Inter",
                                color: "#6A76DB",
                                lineHeight: '20px',
                            }}
                        >
                            Evolution Metrics
                        </ListSubheader>
                        {optionsForAdditionalColumns
                            .filter(option => option.metric_group === "Metrics Evolution")
                            .map((option) => (
                                <MenuItem
                                    key={option.metric_name}
                                    value={option.metric_name}
                                    sx={{ fontSize: "12px", color: "black", fontFamily: "Inter" }}
                                >
                                    <Checkbox
                                        checked={additionnalColumns.includes(option.metric_name)}
                                        sx={{
                                            "& .MuiSvgIcon-root": {
                                                fontSize: 12,
                                            },
                                        }}
                                    />
                                    <ListItemText
                                        primary={option.metric_label}
                                        primaryTypographyProps={{
                                            style: { fontSize: "12px", fontFamily: "Inter", color: "black" },
                                        }}
                                    />
                                </MenuItem>
                            ))}
                    </CustomFormControl>


                </div>

            </div>

            <div className="query-builder-container">
                <label>METRIC ALERT RULES *</label>
                <div className="query-builder">
                    {tree && (
                        <Query
                            {...config}
                            value={tree || null}
                            onChange={onChange}
                            renderBuilder={(props) => <Builder {...props} />}
                        />
                    )}
                </div>
            </div>

            <RecipientsField
                value={alertRecipients}
                onChange={handleRecipientsChange}
                error={errors.alertRecipients}
            />

            <div className="button-container">
                <CustomButtonWithIcon
                    color="#b0acac"
                    hoverColor="#12794F"
                    titleColor="#373F41"
                    hoverTitleColor={"white"}
                    width={"120px"}
                    height={"24px"}
                    borderRadius="20px"
                    textStyle={{
                        fontFamily: 'Inter',
                        fontSize: '12px',
                        fontWeight: 500,
                        lineHeight: '14.52px',
                        textAlign: 'center',
                    }}
                    onClick={handleSubmit}

                >
                    Save Alert
                </CustomButtonWithIcon>

                <CustomButtonWithIcon
                    color="#b0acac"
                    hoverColor="#12794F"
                    titleColor="#373F41"
                    hoverTitleColor={"white"}
                    width={"120px"}
                    height={"24px"}
                    borderRadius="20px"
                    textStyle={{
                        fontFamily: 'Inter',
                        fontSize: '12px',
                        fontWeight: 500,
                        lineHeight: '14.52px',
                        textAlign: 'center',
                    }}
                    onClick={onClose}

                >
                    Cancel
                </CustomButtonWithIcon>
            </div>
        </div>
    );
};

export default AlertDisplayAdsForm;